import { Button, Input } from 'antd';
import { FormOutlined, SearchOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import styled from 'styled-components';
import { LogoCountCard } from '../../../shared/LogoCountCard';
import { SourceConfig, SourceBuilderState, StepProps } from './types';
import { IngestionSourceBuilderStep } from './steps';
import useGetSourceLogoUrl from './useGetSourceLogoUrl';
import { CUSTOM } from './constants';
import { ANTD_GRAY } from '../../../entity/shared/constants';
import { useTranslation } from '../../../../i18n/useTranslation';

const Section = styled.div`
    display: flex;
    flex-direction: column;
    padding-bottom: 12px;
`;

const PlatformListContainer = styled.div`
    gap: 8px;
    display: flex;
    flex-wrap: wrap;

    .stats-tile-card {
        margin: 0;
        width: auto;
        height: 110px;
        box-shadow: none;
        min-width: 160px;
        flex: 0 1 calc(25% - 6px);
        border: 1px solid #e9e9ea;

        .tile-logo {
            img,
            svg {
                width: 48px;
                height: 48px;
                max-height: none;
            }
        }

        h5 {
            font-size: 14px;
            color: #4c4e54;
            margin: 8px 0 0 0;
        }

        @media (max-width: 768px) {
            width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
        }
    }
`;

const StyledSearchBar = styled(Input)`
    margin: 0 0 24px 0;
`;

interface SourceOptionProps {
    source: SourceConfig;
    onClick: () => void;
}

function SourceOption({ source, onClick }: SourceOptionProps) {
    const { name, displayName } = source;

    const logoUrl = useGetSourceLogoUrl(name);
    let logoComponent;
    if (name === CUSTOM) {
        logoComponent = <FormOutlined style={{ color: ANTD_GRAY[8], fontSize: 28 }} />;
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.code === 'Space') {
            e.preventDefault();
            onClick();
        }
    };

    return (
        <LogoCountCard
            isIngestion
            onClick={onClick}
            handleKeyDown={handleKeyDown}
            name={displayName}
            logoUrl={logoUrl}
            logoComponent={logoComponent}
        />
    );
}

/**
 * Component responsible for selecting the mechanism for constructing a new Ingestion Source
 */
export const SelectTemplateStep = ({ state, updateState, goTo, cancel, ingestionSources }: StepProps) => {
    const { t } = useTranslation();
    const [searchFilter, setSearchFilter] = useState('');

    const onSelectTemplate = (type: string) => {
        const newState: SourceBuilderState = {
            ...state,
            config: undefined,
            type,
        };
        updateState(newState);
        goTo(IngestionSourceBuilderStep.DEFINE_RECIPE);
    };

    const filteredSources = ingestionSources.filter(
        (source) => source.displayName.includes(searchFilter) || source.name.includes(searchFilter),
    );

    return (
        <>
            <Section>
                <StyledSearchBar
                    placeholder={t('Search ingestion sources')}
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    allowClear
                    prefix={<SearchOutlined />}
                />
                <PlatformListContainer>
                    {filteredSources.map((source) => (
                        <SourceOption key={source.urn} source={source} onClick={() => onSelectTemplate(source.name)} />
                    ))}
                </PlatformListContainer>
            </Section>
            <Button onClick={cancel}>{t('Cancel')}</Button>
        </>
    );
};
